/**
 * Client half of the studio first-load bootstrap.
 *
 * `prefetchStudioBootstrap()` fires ONE request to `/api/studio/bootstrap` (see
 * the server `lib/studio/bootstrap.ts`) and caches the keyed result. Each
 * existing first-load fetcher then calls `getBootstrapSlice(key)` and uses that
 * slice instead of hitting its own endpoint — collapsing N first-load requests
 * (each a cold-starting lambda) into one, fired in parallel with the session.
 *
 * Slices are consume-once: a slice is served exactly for the first read, then
 * `null` forever after, so later refetches (TTL expiry, window focus, post-
 * mutation) always go to the live endpoint. If the bootstrap request fails or
 * omits a slice, `getBootstrapSlice` returns `null` and the caller falls back
 * to its own endpoint — bootstrap is a pure optimization, never a dependency.
 *
 * ── Tying a future first-load read in ──
 * Add the server slice (see lib/studio/bootstrap.ts), then in that read's
 * fetcher do: `const boot = await getBootstrapSlice<T>("key"); const data =
 * boot ?? (await fetch("/api/…").then((r) => r.json()));`.
 */

const BOOTSTRAP_URL = "/api/studio/bootstrap";
const BOOTSTRAP_TIMEOUT_MS = 8000;

let inflight: Promise<Record<string, unknown>> | null = null;
let resolved: Record<string, unknown> | null = null;
const consumed = new Set<string>();

/**
 * Fire the single bootstrap request (idempotent — safe to call from multiple
 * mount points). Resolves to the keyed slice map, or `{}` on any failure so
 * callers cleanly fall back to their own endpoints.
 */
export function prefetchStudioBootstrap(): Promise<Record<string, unknown>> {
  if (typeof window === "undefined") return Promise.resolve({});
  if (resolved) return Promise.resolve(resolved);
  if (inflight) return inflight;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), BOOTSTRAP_TIMEOUT_MS);

  inflight = fetch(BOOTSTRAP_URL, {
    credentials: "include",
    signal: controller.signal,
  })
    .then((r) => (r.ok ? (r.json() as Promise<Record<string, unknown>>) : {}))
    .then((data) => {
      resolved = data ?? {};
      return resolved;
    })
    .catch(() => {
      // Network / timeout / abort — clear inflight so a later call can retry,
      // and let every slice fall back to its own endpoint this round.
      inflight = null;
      return {} as Record<string, unknown>;
    })
    .finally(() => clearTimeout(timer));

  return inflight;
}

/**
 * Return a first-load slice ONCE (then `null` forever), or `null` if bootstrap
 * failed / the slice is absent. Callers use the returned value if non-null and
 * otherwise fetch their own endpoint.
 */
export async function getBootstrapSlice<T = unknown>(
  key: string,
): Promise<T | null> {
  if (typeof window === "undefined") return null;
  if (consumed.has(key)) return null;

  const data = await prefetchStudioBootstrap();
  const value = data?.[key];
  if (value === undefined || value === null) return null;

  consumed.add(key);
  return value as T;
}
